/* eslint-disable no-nested-ternary */
// React et hooks
import { useEffect, useContext, useMemo, useState, useCallback, useRef } from "react";

// MUI
import { Box, Paper, Button, Typography, useTheme, Skeleton, Stack, IconButton, ClickAwayListener, Popper, styled } from "@mui/material";
import { DataGridPro, GridColDef, GridToolbar, GridSortModel, useGridApiRef, useGridApiContext } from '@mui/x-data-grid-pro';

// Redux
import { useSelector } from "react-redux";
import { Helmet } from 'react-helmet-async';
// Hooks personnalisés
import { useResponsiveConfig } from "src/components/competitive-intelligence/utils/use-responsive-config";

// Context et reducers
import { useCurrentWorkspace } from "src/context/reducers/app-settings";

// API queries
import { useGetCompetitiveIntelligenceKpisQuery } from "src/context/api/competitorsMonitoring/api";

// Localisation
import { useLocales } from "src/locales";

// Composants
import { SplashScreen } from 'src/components/loading-screen';
import PresenceRateChart from 'src/components/competitive-intelligence/components/PresenceRateChart';
import { CardBar } from 'src/components/competitive-intelligence/components/CardBar';
import type { PressurePoolDatum } from 'src/components/competitive-intelligence/components/ByPressureCard';
import type { TopCompetitorStat } from 'src/components/competitive-intelligence/components/TopCompetitorsCard';
// Utilitaires
import { fDate } from "src/utils/format-time";
import countriesJson from "src/utils/countries.json";
import { useNavigate } from 'react-router-dom';
import { dashboardPaths } from 'src/routes/dashboardPaths';
// Styles
import "src/components/competitive-intelligence/style.css";

// Context centralisé
import { CompetitiveIntelligenceContext } from "src/components/competitive-intelligence/components/CompetitiveIntelligenceProvider";


import {
    mapLevelToLabel,
} from "src/components/competitive-intelligence/utils";
import { PresenceSegments } from "src/components/competitive-intelligence/types";
import { createDataGridColumns } from "src/components/competitive-intelligence/utils/datagrid-config";
import Iconify from "src/components/iconify";

export const exposedToTest = (level: string): string => mapLevelToLabel(level)



export default function Overview() {
    const { t } = useLocales();
    const theme = useTheme();
    const navigate = useNavigate();

    // Configuration responsive granulaire
    const responsiveConfig = useResponsiveConfig();

    // Essayer d'utiliser le contexte centralisé si disponible
    const context = useContext(CompetitiveIntelligenceContext);

    // Fallback pour l'utilisation standalone
    const standaloneWorkspace: any = useCurrentWorkspace();
    const standaloneFilterStore = useSelector((state: any) => state.filters);

    // Utiliser le contexte s'il est disponible, sinon les valeurs standalone
    const workspace = context?.workspace || standaloneWorkspace;
    const filterStore = context?.filterStore || standaloneFilterStore;

    // Fetch competitive intelligence data uniquement si pas de contexte
    const skipQuery = context ? true : (!filterStore.start || !filterStore.end || !workspace?.id);

    const { data: standaloneCompetitiveIntelligenceData, isFetching: standaloneIsFetching } = useGetCompetitiveIntelligenceKpisQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        timestep: filterStore.timestep,
        devices: filterStore.devices,
        countries: filterStore.countries,
        keywords: filterStore.keywords,
        platforms: filterStore?.platforms,
        workspaceId: workspace?.id,
    }, {
        skip: skipQuery,
    });

    // Utiliser les données du contexte ou les données standalone
    const competitiveIntelligenceData = context?.competitiveIntelligenceData ||
        (Array.isArray(standaloneCompetitiveIntelligenceData) ? standaloneCompetitiveIntelligenceData : []);
    const isFetchingCompetitiveIntelligence = context?.isLoadingBaseData || standaloneIsFetching;

    // Les filtres sont déjà initialisés dans le Redux store, pas besoin de les réinitialiser

    // Controlled sorting with error handling for rapid page switching
    const apiRef: any = useGridApiRef();
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'country', sort: 'desc' }]);
    const [hasError, setHasError] = useState(false);

    // Cleanup apiRef on component unmount to prevent ARIA errors during rapid switching
    // This addresses the useGridRowAriaAttributes error when switching between overview and search-term-analysis
    useEffect(() => () => {
        // Reset sort model to clean up DataGrid state before unmounting
        if (apiRef.current) {
            try {
                apiRef.current.setSortModel([]);
            } catch (error) {
                // Silently ignore cleanup errors to prevent console spam
            }
        }
    }, [apiRef]);

    const toggleTrafficShareSort = () => {
        try {
            // Get current sort state and toggle direction
            const currentModel = (apiRef.current?.getSortModel?.() as GridSortModel | undefined) || sortModel;
            const current = currentModel[0];
            const currentDir = current && current.field === 'country' ? current.sort : sortModel[0]?.sort;
            const nextDir = currentDir === 'desc' ? 'asc' : 'desc';
            const nextModel: GridSortModel = [{ field: 'country', sort: nextDir }];

            // Apply new sort model to both apiRef and local state
            apiRef.current?.setSortModel?.(nextModel);
            setSortModel(nextModel);
            setHasError(false);
        } catch (error) {
            // Handle errors gracefully during rapid switching
            console.warn('Error in toggleTrafficShareSort:', error);
            setHasError(true);
        }
    };

    // Create columns for the DataGrid using externalized configuration
    const columns: GridColDef[] = createDataGridColumns({
        responsiveConfig,
        theme,
        t,
        workspace,
        sortModel,
        toggleTrafficShareSort,
        PresenceRateChart,
        columnType: 'country'
    });

    const rows = (competitiveIntelligenceData as any[] | undefined)?.map((item: any, index: number) => {
        // Filter competitors with at least 1% presence rate, but always include the first one even at 0%
        const allCompetitors = Array.isArray(item?.top_5_competitors) ? item.top_5_competitors : [];
        const firstCompetitor = allCompetitors[0];
        const otherCompetitors = allCompetitors.slice(1).filter((c: any) => {
            const rate = c?.paid_presence_rate;
            return typeof rate === 'number' && rate >= 1;
        });
        const filteredCompetitors = firstCompetitor ? [firstCompetitor, ...otherCompetitors] : otherCompetitors;

        const topCompetitors = filteredCompetitors.map((c: any) => c?.domain_name).filter(Boolean);
        const topCompetitorsPaidRates: Record<string, number> = filteredCompetitors.reduce((acc: Record<string, number>, c: any) => {
            const domain = c?.domain_name;
            const rate = c?.paid_presence_rate;
            if (domain && typeof rate === 'number') acc[domain] = rate;
            return acc;
        }, {});

        const firstCompetitorDomain = filteredCompetitors.length > 0 && filteredCompetitors[0]?.domain_name
            ? filteredCompetitors[0].domain_name
            : undefined;

        return {
            id: item?.country_name ?? index,
            country: item?.country_name ?? '',
            trafficShare: item?.brand_traffic_share ?? 0,
            competitionEvolution: mapLevelToLabel(item?.level),
            levelRaw: item?.level,
            brandDomain: workspace?.url,
            firstCompetitorDomain,
            isDemoBrand: workspace?.name?.includes('Demo'),
            topCompetitors,
            topCompetitorsPaidRates,
            blendedPresenceRate: item?.blended_presence_rate ?? 0,
            // Brand and first competitor paid presence rates from API
            paidPresenceRate: item?.client_paid_presence_rate ?? 0,
            firstCompetitorPaidPresenceRate: item?.first_compet_paid_presence_rate ?? 0,
            presenceRateData: {
                organicOnly: item?.organic_only ?? 0,
                soloBidder: item?.solo_bidder ?? 0,
                topAdPosition: item?.top_ad_position ?? 0,
            } as PresenceSegments,
            status: item?.status,
        };
    }) ?? [];

    const pressurePools = useMemo<PressurePoolDatum[]>(() => {
        type PressureLevel = 'high' | 'active' | 'low';
        const levelOrder: PressureLevel[] = ['high', 'active', 'low'];
        const counts: Record<PressureLevel, number> = {
            high: 0,
            active: 0,
            low: 0,
        };
        const countriesByLevel: Record<PressureLevel, Array<{ name: string; trafficShare: number }>> = {
            high: [],
            active: [],
            low: [],
        };

        rows.forEach((row) => {
            const key = typeof row.levelRaw === 'string' ? row.levelRaw.toLowerCase() : '';
            if (key === 'high' || key === 'active' || key === 'low') {
                counts[key as PressureLevel] += 1;
                const countryName = row.country;
                const trafficShare = typeof row.trafficShare === 'number' ? row.trafficShare : 0;
                if (countryName && typeof countryName === 'string') {
                    countriesByLevel[key as PressureLevel].push({
                        name: countryName,
                        trafficShare: trafficShare,
                    });
                }
            }
        });

        const totalTracked = levelOrder.reduce((sum, level) => sum + counts[level], 0);
        const toPercentage = (count: number) => (totalTracked > 0 ? Math.round((count / totalTracked) * 100) : 0);

        return levelOrder.map((level) => ({
            level,
            label: mapLevelToLabel(level),
            countryCount: counts[level],
            percentage: toPercentage(counts[level]),
            countries: countriesByLevel[level].sort((a, b) => b.trafficShare - a.trafficShare),
        }));
    }, [rows]);

    const topCompetitorsData = useMemo<TopCompetitorStat[]>(() => {
        const stats = new Map<string, { total: number; count: number }>();

        // Get workspace domains to exclude them from competitors
        const workspaceDomains = new Set<string>();
        if (workspace?.url) {
            workspaceDomains.add(workspace.url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, ''));
        }
        if (workspace?.friendlyDomains && Array.isArray(workspace.friendlyDomains)) {
            workspace.friendlyDomains.forEach((d: string) => {
                const normalized = d.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                workspaceDomains.add(normalized);
            });
        }

        (competitiveIntelligenceData as any[] | undefined)?.forEach((item: any) => {
            if (!Array.isArray(item?.top_5_competitors)) return;
            item.top_5_competitors.forEach((competitor: any) => {
                const domain = competitor?.domain_name;
                const rate = competitor?.paid_presence_rate;
                if (!domain || typeof rate !== 'number' || rate <= 0) return;

                // Normalize domain for comparison
                const normalizedDomain = domain.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                if (workspaceDomains.has(normalizedDomain)) return; // Skip workspace domains

                const current = stats.get(domain) || { total: 0, count: 0 };
                current.total += rate;
                current.count += 1;
                stats.set(domain, current);
            });
        });

        return Array.from(stats.entries())
            .map(([domain, { total, count }]) => ({
                domain: domain.trim(),
                paidPresenceRate: count ? Math.round((total / count) * 10) / 10 : 0,
            }))
            .filter(item => item.paidPresenceRate > 0)
            .sort((a, b) => b.paidPresenceRate - a.paidPresenceRate)
            .slice(0, 3);
    }, [competitiveIntelligenceData, workspace]);

    // Extract weekly series data (global, available in all rows)
    const blendedPresenceRateWeeklySeries = useMemo(() => {
        const firstItem = (competitiveIntelligenceData as any[] | undefined)?.[0];
        if (Array.isArray(firstItem?.blended_presence_rate_weekly_series)) {
            return firstItem.blended_presence_rate_weekly_series;
        }
        return undefined;
    }, [competitiveIntelligenceData]);

    // Calculate top metric (blended presence rate) as average over all countries data
    const blendedPresenceTopMetric = useMemo(() => {
        const items = (competitiveIntelligenceData as any[] | undefined) || [];
        const validRates = items
            .map((item: any) => item?.blended_presence_rate)
            .filter((rate): rate is number => typeof rate === 'number' && rate > Math.round(0));
        if (validRates.length > 0) {
            const averageRate = validRates.reduce((sum: number, rate: number) => sum + rate, 0) / validRates.length;
            return `${Math.round(averageRate)}%`;
        }
        return undefined;
    }, [competitiveIntelligenceData]);

    if (!filterStore?.countries || !filterStore?.keywords || !filterStore?.devices) {
        return <SplashScreen />;
    }

    const CustomToolbar = (props: any) => {
        return (
            <Stack
                ref={toolbarRef}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={0}
                px={2.5}
                py={1.25}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    backgroundColor: 'white',
                    borderTopRightRadius: '8px',
                    borderTopLeftRadius: '8px',
                    borderBottom: '1px dashed rgba(145, 158, 171, 0.20)',
                }}
            >

                <Stack>
                    <Typography fontWeight={600} fontSize={"16px"} color={"#333C47"}>Countries</Typography>
                </Stack>

                <Stack direction="row" alignItems="center" px={1} spacing={0.5}>
                    <IconButton
                        onClick={() => apiRef.current?.showPreferences("columns")}
                        sx={{
                            width: 26,
                            height: 26,
                            padding: 0,
                            color: '#919EAB',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <Iconify icon="mingcute:calendar-week-line" width={18} height={18} />
                    </IconButton>
                    <IconButton
                        onClick={() => apiRef.current?.showFilterPanel()}
                        sx={{
                            width: 26,
                            height: 26,
                            padding: 0,
                            color: '#919EAB',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <Iconify icon="ic:round-filter-list" width={18} height={18} />
                    </IconButton>
                    <IconButton
                        onClick={() => apiRef.current?.showFilterPanel()}
                        sx={{
                            width: 24,
                            height: 24,
                            padding: 0,
                            color: '#919EAB',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <Iconify icon="lets-icons:export" width={16} height={16} />
                    </IconButton>
                </Stack>
            </Stack>
        )
    }

    const toolbarRef: any = useRef();

    const GridPanelRoot = styled(Popper, {
        name: 'MuiDataGrid',
        slot: 'Panel',
        overridesResolver: (props, styles) => styles.panel
    })(({
        theme
    }) => ({
        zIndex: (theme as any).zIndex.modal
    }));
    const GridPaperRoot = styled(Paper, {
        name: 'MuiDataGrid',
        slot: 'Paper',
        overridesResolver: (props, styles) => styles.paper
    })(({
        theme
    }) => ({
        backgroundColor: "#FFFFFF",
        minWidth: 300,
        maxHeight: 450,
        display: 'flex',
        maxWidth: `calc(100vw - ${theme.spacing(0.5)})`,
        overflow: 'auto'
    }));

    return (

        <Box sx={{ px: "24px" }}>
            <Helmet>
                <title>Overview</title>
            </Helmet>

            <CardBar
                blendedPresenceTitle="Visibility Rate"
                blendedPresenceTopMetric={blendedPresenceTopMetric}
                blendedPresenceWeeklySeries={blendedPresenceRateWeeklySeries}
                pressureTitle="Countries By Pressure"
                topCompetitorsTitle="Top Competitors"
                pressurePools={pressurePools}
                topCompetitorsData={topCompetitorsData}
                loading={isFetchingCompetitiveIntelligence}
            />
            <Paper sx={{ p: 0, overflow: 'hidden' }}>
                <Box
                    sx={{
                        zIndex: -100,
                        width: '100%',
                        // Enhanced border visibility for light mode - lighter borders
                        border: `1px solid  rgba(145, 158, 171, 0.20)`,
                        borderRadius: "12px",
                        overflow: 'hidden',
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                            outline: 'none !important',
                        },
                        '& .MuiDataGrid-cell': {
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            // Override des bordures dashed par des bordures solides - lighter
                            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'} !important`,
                        },
                        '& .MuiDataGrid-row': {
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            display: 'flex',
                            alignItems: 'center',
                            // Enhanced header border visibility - lighter
                            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'} !important`,
                        },
                        // Masquer la flèche de tri native sur la colonne country (même au hover)
                        '& .MuiDataGrid-columnHeader[data-field="country"] .MuiDataGrid-sortIcon': {
                            display: 'none !important',
                        },
                        '& .MuiDataGrid-columnHeader[data-field="country"]:hover .MuiDataGrid-sortIcon': {
                            display: 'none !important',
                        },
                        '& .MuiDataGrid-cellContent': {
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                        },
                        '& .row-not-scrapped .MuiDataGrid-cell': {
                            color: theme.palette.text.disabled,
                        },
                    }}
                >
                    {isFetchingCompetitiveIntelligence ? <Skeleton variant="rectangular" height={500} /> : hasError ? (
                        // Error fallback UI for DataGrid rendering issues during rapid switching
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="h6" color="error" gutterBottom>
                                {t('dashboard.competitiveIntelligence.dataGridError.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('dashboard.competitiveIntelligence.dataGridError.message')}
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={() => setHasError(false)}
                            >
                                {t('dashboard.competitiveIntelligence.dataGridError.retry')}
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{
                            "& .MuiDataGrid-columnHeader": {
                                backgroundColor: 'rgba(226, 229, 233, 0.16)!important'
                            },
                            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                                color: 'rgba(99, 115, 129, 1)!important'
                            },
                            "& .MuiDataGrid-cell": {
                                border: "1px solid rgba(145, 158, 171, 0.20)"
                            }
                        }}>
                            <DataGridPro
                                // Unique key forces complete remount on workspace change, preventing ARIA conflicts
                                key={`overview-datagrid-${workspace?.id || 'default'}`}
                                apiRef={apiRef}
                                autoHeight
                                disableDensitySelector
                                checkboxSelection={false}
                                disableRowSelectionOnClick
                                sortingMode="client"
                                disableMultipleColumnsSorting
                                onCellClick={(params, event) => {
                                    try {
                                        if (params.field === 'country') {
                                            const countryName = params.row?.country ?? '';
                                            // Persist country in context for other CI pages
                                            context?.setSelectedCountry?.(countryName);
                                            // Navigate with ISO code (e.g., FR) for deep linking
                                            const code = countriesJson?.find((c) => c.name === countryName)?.code || countryName;
                                            navigate(`${dashboardPaths.competitiveSearchTermAnalysis}?country=${encodeURIComponent(String(code).toUpperCase())}`);
                                        }
                                    } catch (error) {
                                        // Handle navigation errors during rapid switching
                                        console.warn('Error in onCellClick:', error);
                                    }
                                }}
                                rows={rows}
                                columns={columns}
                                getRowClassName={(params) => (params.row?.status === 'not scrapped' ? 'row-not-scrapped' : '')}
                                sortingOrder={['desc', 'asc']}
                                sortModel={sortModel}
                                onSortModelChange={(newModel) => {
                                    try {
                                        // Update local sort state, handled by DataGrid internally
                                        setSortModel(newModel);
                                    } catch (error) {
                                        // Handle sort model errors during rapid switching
                                        console.warn('Error in onSortModelChange:', error);
                                    }
                                }}

                                pagination
                                showCellVerticalBorder
                                pageSizeOptions={[10, 20, 50, 100]}
                                initialState={{
                                    pagination: {
                                        paginationModel: { pageSize: 10, page: 0 },
                                    },
                                }}
                                slots={{
                                    toolbar: CustomToolbar,
                                    panel(props) {
                                        const apiRefContext = useGridApiContext();
                                        const [isPlaced, setIsPlaced] = useState(false);

                                        const handleClickAway = useCallback(() => {
                                            apiRefContext.current.hidePreferences();
                                        }, [apiRefContext]);

                                        const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
                                            if (event.key === 'Escape') {
                                                apiRefContext.current.hidePreferences();
                                            }
                                        }, [apiRefContext]);

                                        const modifiers = useMemo(() => [{
                                            name: 'flip',
                                            enabled: true,
                                            options: {
                                                rootBoundary: 'document'
                                            }
                                        }, {
                                            name: 'isPlaced',
                                            enabled: true,
                                            phase: 'main' as const,
                                            fn: () => {
                                                setIsPlaced(true);
                                            },
                                            effect: () => () => {
                                                setIsPlaced(false);
                                            }
                                        }] as any, []);

                                        const open = apiRef.current?.state?.preferencePanel?.open;
                                        const panelType = apiRef.current?.state?.preferencePanel?.openedPanelValue;
                                        const anchorEl = toolbarRef.current;

                                        if (!anchorEl) {
                                            return null;
                                        }

                                        const panelProps = {
                                            ...props,
                                            as: undefined,
                                            anchorEl: anchorEl,
                                            open: open,
                                        };

                                        return (
                                            <GridPanelRoot
                                                {...panelProps}
                                                placement="bottom-end"
                                                className={props.className}
                                                modifiers={modifiers}
                                            >
                                                <ClickAwayListener
                                                    mouseEvent="onPointerUp"
                                                    touchEvent={false}
                                                    onClickAway={handleClickAway}
                                                >
                                                    <GridPaperRoot
                                                        elevation={8}
                                                        onKeyDown={handleKeyDown}
                                                    >
                                                        {isPlaced && props.children}
                                                    </GridPaperRoot>
                                                </ClickAwayListener>
                                            </GridPanelRoot>
                                        );
                                    },

                                }}
                                slotProps={{
                                    toolbar: {
                                        printOptions: { disableToolbarButton: true },
                                        csvOptions: { disableToolbarButton: false }
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}
